import { Controller } from "@hotwired/stimulus";
import * as Turbo from "@hotwired/turbo";
import { handleStaleResponse } from "../lib/handle_stale_response";
import { autosaveDraft, flush, cancel as cancelAutosave } from "../components/lib/autosave_draft";

export default class extends Controller {
  static targets = ["submitButton"];
  static values = {
    submitUrl: String,
    draftUrl: String,
  };

  connect() {
    this.submitting = false;
    this.element.querySelectorAll(".fill-blank-input").forEach((input) => {
      this.#autoSize(input);
    });
    this.#refreshSubmitButton();
  }

  disconnect() {
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);
  }

  resize(event) {
    this.#autoSize(event.target);
  }

  inputChanged(event) {
    this.#autoSize(event.target);
    this.#refreshSubmitButton();
    this.#scheduleAutosave();
  }

  // Enter flushes the pending autosave debounce — it does NOT submit the
  // exercise. preventDefault stops any implicit form submission.
  flushDraft(event) {
    event?.preventDefault();
    if (this.hasDraftUrlValue) flush(this.draftUrlValue);
  }

  submit(event) {
    event?.preventDefault();
    if (this.submitting) return;
    if (this.hasSubmitButtonTarget && this.submitButtonTarget.disabled) return;

    this.submitting = true;
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.disabled = true;
    if (this.hasDraftUrlValue) cancelAutosave(this.draftUrlValue);

    const token = document.querySelector("meta[name=csrf-token]")?.content || "";
    const params = new URLSearchParams();
    this.#inputs().forEach((input) => {
      params.append(`submitted[${input.dataset.blankId}]`, input.value);
    });

    fetch(this.submitUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "text/vnd.turbo-stream.html",
        "X-CSRF-Token": token,
      },
      body: params.toString(),
    })
      .then((r) => {
        if (handleStaleResponse(this, r)) return null;
        return r.text();
      })
      .then((html) => {
        if (html) Turbo.renderStreamMessage(html);
      })
      .finally(() => {
        this.submitting = false;
        // Re-enable the submit button on stale/error responses. A stale
        // response means the exercise is NOT terminal, so a plain refresh
        // (which derives disabled from filled inputs) is correct.
        this.#refreshSubmitButton();
      });
  }

  #inputs() {
    return Array.from(this.element.querySelectorAll(".fill-blank-input"));
  }

  #refreshSubmitButton() {
    if (!this.hasSubmitButtonTarget) return;
    const someFilled = this.#inputs().some(
      (i) => !i.disabled && i.value.trim().length > 0,
    );
    this.submitButtonTarget.disabled = !someFilled || this.submitting;
  }

  #scheduleAutosave() {
    if (this.submitting || !this.hasDraftUrlValue) return;
    autosaveDraft({
      url: this.draftUrlValue,
      body: { submitted: this.#currentDraft() },
    });
  }

  #currentDraft() {
    const draft = {};
    this.#inputs().forEach((input) => {
      if (input.disabled) return;
      draft[input.dataset.blankId] = input.value;
    });
    return draft;
  }

  #autoSize(input) {
    const minSize = parseInt(input.dataset.minSize) || 5;
    const len = input.value.length;
    input.size = Math.max(len > 0 ? len + 1 : minSize, minSize);
  }
}
